import React from 'react'

import { FormattedMessage } from 'react-intl'
import Slider from 'react-slick'

import { Icon } from '@tribegroup'
import { IAgentSourcedCreator, ISelectedWork, Provider } from '../types'
import {
  HandleLink,
  PlatformBlock,
  PlatformHandle,
  PlatformHeader,
  WorkArrow,
  WorkArrowGroup,
  WorkBody,
  WorkCard,
  WorkDate,
  WorkDot,
  WorkDotsList,
  WorksRoot,
  WorksSlider,
  WorkStat,
  WorkStatLabel,
  WorkStatRow,
  WorkStatValue,
  WorkThumb,
  WorkThumbLink,
  WorkTitle,
  WorkVideoBadge,
} from './SelectedWorks.styled'

// QA follow-up (BRAND-9490, ticket comment 102422): these arrows used to be
// react-slick's `prevArrow`/`nextArrow` slots, which render absolutely
// positioned INSIDE the slider's own stacking context — that's what put
// them low-contrast over the thumbnail edges. They now live in the channel
// handle header row instead (see `WorkArrowGroup` in the JSX below) and are
// driven imperatively via the `Slider` ref (`slickPrev`/`slickNext`), so
// `disabled` has to be computed by hand from `currentSlide` rather than read
// off a react-slick-injected `slick-disabled` className.
interface IWorkArrowProps {
  direction: 'left' | 'right'
  disabled: boolean
  onClick: VoidFunction
}

const SelectedWorksArrow: React.FC<IWorkArrowProps> = ({ direction, disabled, onClick }) => {
  // These arrows are a `div[role=button]`, not a real <button>, so they get
  // no keyboard behaviour for free — Enter/Space must be wired up by hand.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick()
    }
  }

  return (
    <WorkArrow
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={direction === 'left' ? 'Previous works' : 'More works'}
      data-testid={`selected-works-arrow-${direction}`}
    >
      <Icon glyph={`chevron-${direction}`} size={1} />
    </WorkArrow>
  )
}

const PROVIDER_GLYPH: Record<Provider, string> = {
  instagram: 'social-instagram-colour',
  tiktok: 'social-tiktok-colour',
  youtube: 'social-youtube-colour',
}

// Maps selected_works[].type → existing submission-type glyph set (same set the
// Inbox `getThumbnailIcon` resolves to).
const WORK_TYPE_GLYPH: Record<string, string> = {
  video: 'video',
  short: 'shorts',
  shorts: 'shorts',
  reel: 'reels',
  reels: 'reels',
  tiktok: 'tiktok',
  carousel: 'carousel',
  story: 'story',
  standard: 'video',
}

const formatStat = (n: number | null | undefined): string =>
  n == null ? '—' : n.toLocaleString('en-AU')

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

interface IProps {
  creator: IAgentSourcedCreator
  works: ISelectedWork[]
}

const VISIBLE_WORKS = 3

const SelectedWorks: React.FC<IProps> = ({ creator, works }) => {
  // Drives paging imperatively (BRAND-9490 QA follow-up) now that the arrows
  // live in the header row instead of react-slick's own `prevArrow`/
  // `nextArrow` slots, and the page dots jump directly to a page — neither
  // is wired up automatically by react-slick without those slots/its own
  // `dots` prop, so both go through the same ref.
  const sliderRef = React.useRef<Slider>(null)
  // Guards against the carousel firing a work-thumbnail navigation click at
  // the end of a drag/swipe gesture (same pattern as the BrandFans submissions
  // carousel's `disableLinkClick`).
  const [isDragging, setIsDragging] = React.useState(false)
  // Own page-dot state (BRAND-9490): react-slick's built-in `dots` are no
  // longer used (see WorksSlider render below), so the current slide has to
  // be tracked by hand off the same before/afterChange lifecycle the drag
  // guard already hooks into. `beforeChange`'s `nextSlide` is applied
  // immediately so the active dot flips in the same tick as a click (matches
  // the pre-existing test expectation, and how react-slick's own dots
  // behaved); `afterChange` re-confirms once the transition settles.
  const [currentSlide, setCurrentSlide] = React.useState(0)
  const handleBeforeChange = (_current: number, nextSlide: number): void => {
    setIsDragging(true)
    setCurrentSlide(nextSlide)
  }
  const handleAfterChange = (current: number): void => {
    setIsDragging(false)
    setCurrentSlide(current)
  }
  const guardLinkClick = (event: React.SyntheticEvent): void => {
    if (isDragging) event.preventDefault()
  }

  if (!works.length) return null

  // Dots paginate by PAGE (group of VISIBLE_WORKS slides), same as the old
  // react-slick dot count (`Math.ceil(slideCount / slidesToScroll)`).
  const pageCount = Math.ceil(works.length / VISIBLE_WORKS)
  // react-slick (infinite={false}) clamps the slide it will actually settle
  // on to `slideCount - slidesToShow` — it can't scroll further right than
  // that, since there aren't enough remaining slides to fill the view. When
  // the work count isn't a multiple of VISIBLE_WORKS, that clamped value
  // lands short of the naive `(pageCount - 1) * VISIBLE_WORKS` the last page
  // "should" start at (e.g. 8 works: last page naively starts at slide 6, but
  // clamps to 5), so `Math.floor(currentSlide / VISIBLE_WORKS)` under-shoots
  // by one dot on the final page. Once react-slick has clamped that far
  // right (`currentSlide >= works.length - VISIBLE_WORKS`), it's on the last
  // page by definition — snap straight to the last dot instead of deriving
  // it from the (now-unreliable) slide index.
  const activePage =
    currentSlide >= works.length - VISIBLE_WORKS
      ? pageCount - 1
      : Math.floor(currentSlide / VISIBLE_WORKS)

  // Mirrors react-slick's own `slick-disabled` rule for `infinite: false`
  // (previously read off the injected className — see SelectedWorksArrow
  // above) now that the arrows are no longer react-slick's `prevArrow`/
  // `nextArrow` slots.
  const hasPrev = currentSlide > 0
  const hasNext = currentSlide < works.length - VISIBLE_WORKS

  // QA follow-up (BRAND-9490, ticket comment 102422): page dots were
  // rendered but not clickable. `slickGoTo` jumps straight to the target
  // page's first slide in one step — for the last (possibly short) page
  // this naturally clamps the same way react-slick already clamps arrow
  // navigation (see the `activePage` comment above), so no separate
  // clamping is needed here.
  const handleDotClick = (pageIndex: number): void => {
    sliderRef.current?.slickGoTo(pageIndex * VISIBLE_WORKS)
  }

  const handle =
    creator.creator_data?.handle ??
    creator.creator_data?.username ??
    creator.creator_data?.fullname ??
    ''

  return (
    <WorksRoot>
      <PlatformBlock>
        <PlatformHeader
          data-testid="selected-works-header"
          $hasArrows={works.length > VISIBLE_WORKS}
        >
          <Icon
            glyph={PROVIDER_GLYPH[creator.channel_type] ?? 'social-instagram-colour'}
            size={1.125}
          />
          {creator.creator_data?.url ? (
            <HandleLink href={creator.creator_data.url} target="_blank" rel="noopener noreferrer">
              <PlatformHandle>{handle}</PlatformHandle>
            </HandleLink>
          ) : (
            <PlatformHandle>{handle}</PlatformHandle>
          )}
          {/* QA follow-up (BRAND-9490, ticket comment 102422): arrows moved
              from react-slick's `prevArrow`/`nextArrow` slots (absolutely
              positioned over the thumbnail edges — low-contrast, easy to
              miss) into the upper-right of this handle row instead.
              Review fix (BRAND-9490): still DOM-nested inside this header
              (so `within(header)` assertions and the visual "upper-right of
              the handle row" placement both hold), but `WorkArrowGroup` is
              now `position: absolute` (see SelectedWorks.styled.ts) so it
              overlays the header rather than sizing it — the in-flow
              version made this header grow ~14px taller whenever arrows
              showed, which violated the "no added vertical space" AC. */}
          {works.length > VISIBLE_WORKS && (
            <WorkArrowGroup>
              <SelectedWorksArrow
                direction="left"
                disabled={!hasPrev}
                onClick={() => sliderRef.current?.slickPrev()}
              />
              <SelectedWorksArrow
                direction="right"
                disabled={!hasNext}
                onClick={() => sliderRef.current?.slickNext()}
              />
            </WorkArrowGroup>
          )}
        </PlatformHeader>
        <WorksSlider data-testid="selected-works-slider">
          <Slider
            ref={sliderRef}
            // The arrows now render in the header row above (driven via
            // `sliderRef.slickPrev`/`slickNext`) rather than through
            // react-slick's own `prevArrow`/`nextArrow` slots, so `arrows`
            // stays false — otherwise react-slick would fall back to
            // rendering its own default arrow buttons over the thumbnails.
            arrows={false}
            // react-slick's own built-in dots are NOT used (BRAND-9490): its
            // default `.slick-dots li` / `li button:before` CSS bled through
            // the `appendDots`/`customPaging` overrides, rendering a
            // malformed active dot (oversized, off-color). The dot row is
            // rendered by hand below instead, mirroring the hand-rolled
            // Submission Details carousel dots
            // (`UI/Multiframe/Pagination/Dots`).
            dots={false}
            infinite={false}
            slidesToShow={VISIBLE_WORKS}
            slidesToScroll={VISIBLE_WORKS}
            beforeChange={handleBeforeChange}
            afterChange={handleAfterChange}
          >
            {works.map((work, idx) => {
              const badgeGlyph = WORK_TYPE_GLYPH[work.type] ?? null
              return (
                <WorkCard key={work.post_id ?? idx}>
                  {work.link ? (
                    <WorkThumbLink
                      href={work.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={guardLinkClick}
                    >
                      <WorkThumb>
                        {work.thumbnail && <img src={work.thumbnail} alt={work.title ?? ''} />}
                        {badgeGlyph && (
                          <WorkVideoBadge>
                            <Icon glyph={badgeGlyph} size={2} color="white" />
                          </WorkVideoBadge>
                        )}
                      </WorkThumb>
                    </WorkThumbLink>
                  ) : (
                    <WorkThumb>
                      {work.thumbnail && <img src={work.thumbnail} alt={work.title ?? ''} />}
                      {badgeGlyph && (
                        <WorkVideoBadge>
                          <Icon glyph={badgeGlyph} size={2} color="white" />
                        </WorkVideoBadge>
                      )}
                    </WorkThumb>
                  )}
                  <WorkBody>
                    <WorkDate>{formatDate(work.created)}</WorkDate>
                    <WorkTitle>{work.title ?? work.caption ?? '—'}</WorkTitle>
                    <WorkStatRow>
                      <WorkStat>
                        <WorkStatLabel>
                          <FormattedMessage id="creatorSearch.drawer.work.likes" />
                        </WorkStatLabel>
                        <WorkStatValue>{formatStat(work.stats?.likes)}</WorkStatValue>
                      </WorkStat>
                      <WorkStat>
                        <WorkStatLabel>
                          <FormattedMessage id="creatorSearch.drawer.work.views" />
                        </WorkStatLabel>
                        <WorkStatValue>{formatStat(work.stats?.views)}</WorkStatValue>
                      </WorkStat>
                      <WorkStat>
                        <WorkStatLabel>
                          <FormattedMessage id="creatorSearch.drawer.work.comments" />
                        </WorkStatLabel>
                        <WorkStatValue>{formatStat(work.stats?.comments)}</WorkStatValue>
                      </WorkStat>
                    </WorkStatRow>
                  </WorkBody>
                </WorkCard>
              )
            })}
          </Slider>
          {pageCount > 1 && (
            <WorkDotsList data-testid="selected-works-dots">
              {Array.from({ length: pageCount }, (_, index) => (
                <WorkDot
                  key={index}
                  type="button"
                  active={index === activePage}
                  aria-label={`Selected works page ${index + 1}`}
                  aria-current={index === activePage}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </WorkDotsList>
          )}
        </WorksSlider>
      </PlatformBlock>
    </WorksRoot>
  )
}

export { SelectedWorks }
export default SelectedWorks
